import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, Appointment, AuditLog } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import IntakeAdminPanel from './IntakeAdminPanel';
import CompletionDialog from './billing/CompletionDialog';

interface AppointmentModalProps {
  appointment: Appointment;
  onClose: () => void;
  onUpdate: () => void;
  onEdit: () => void;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({ appointment, onClose, onUpdate, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();
  
  const [activeTab, setActiveTab] = useState<'details' | 'intake' | 'history'>('details');
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);

  const navigate = useNavigate();
  // "Behandlung abschließen" is offered for appointments that have actually
  // taken place (or are taking place now). Today/past + status that isn't
  // already terminal.
  const todayStr = new Date().toISOString().slice(0, 10);
  const isPastOrToday = (appointment.appointment_date || '') <= todayStr;
  const canComplete = isPastOrToday && ['confirmed', 'pending', 'scheduled'].includes(appointment.status);
  // Offers (Kostenvoranschlag) are useful before the appointment.
  const isFuture = !isPastOrToday;
  const canOfferQuote = isFuture && appointment.status !== 'cancelled' && !!appointment.patient_id;

  const startOffer = () => {
    if (!appointment.patient_id) return;
    onClose();
    navigate(
      `/admin/invoices/new?type=offer&patient=${appointment.patient_id}&appointment=${appointment.id}&language=${appointment.language || 'de'}`
    );
  };

  useEffect(() => {
    if (activeTab === 'history') {
      fetchAuditLogs();
    }
  }, [activeTab, appointment.id]);

  const fetchAuditLogs = async () => {
    try {
      setLogsLoading(true);
      const response = await apiService.getAppointmentAuditLogs(appointment.id);
      if (response.data.success) {
        setAuditLogs(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      addToast('Failed to load history', 'error');
    } finally {
      setLogsLoading(false);
    }
  };

  const renderAuditAction = (log: AuditLog) => {
    const inv = (log.new_data || {}) as any;
    const num = inv.number || `#${inv.invoice_id ?? ''}`;
    switch (log.action) {
      case 'create': return 'Termin erstellt';
      case 'update': return 'Termin aktualisiert';
      case 'cancel': return 'Termin storniert';
      case 'delete': return 'Termin gelöscht';
      case 'email_sent': return `E-Mail versendet (${log.old_data?.type || log.new_data?.type || ''})`;
      case 'email_failed': return 'E-Mail-Versand fehlgeschlagen';
      case 'invoice_finalized': return `Rechnung ausgestellt (${num})`;
      case 'offer_finalized': return `Angebot ausgestellt (${num})`;
      case 'cancellation_finalized': return `Stornorechnung ausgestellt (${num})`;
      case 'invoice_paid': return `Rechnung als bezahlt markiert (${num})`;
      case 'invoice_canceled': return `Rechnung storniert (${num})`;
      case 'cancellation_issued': return `Stornorechnung ausgestellt (${num})`;
      case 'invoice_emailed': return `Beleg per E-Mail versendet (${num} an ${inv.to || ''})`;
      case 'invoice_email_failed': return `E-Mail-Versand für ${num} fehlgeschlagen`;
      default: return log.action;
    }
  };

  const renderAuditDetails = (log: AuditLog) => {
    if ((log.action === 'update' || log.action === 'cancel') && log.old_data && log.new_data) {
      const changes: React.ReactNode[] = [];
      for (const key in log.new_data) {
        if (log.new_data[key] !== log.old_data[key] && key !== 'updated_at') {
           changes.push(
             <li key={key} className="text-gray-600">
               <span className="font-medium text-gray-700">{key}:</span> {String(log.old_data[key] || '-')} <span className="mx-1 text-gray-400">➔</span> {String(log.new_data[key] || '-')}
             </li>
           );
        }
      }
      return changes.length > 0 ? <ul className="text-sm mt-2 pl-4 border-l-2 border-gray-200 space-y-1">{changes}</ul> : null;
    }
    if (log.action === 'email_failed' && log.old_data?.error) {
      return <p className="text-sm text-red-600 mt-1">{log.old_data.error}</p>;
    }
    return null;
  };

  const handleStatusChange = async (newStatus: Appointment['status']) => {
    try {
      setLoading(true);
      setError(null);
      
      if (newStatus === 'cancelled') {
        await apiService.cancelAppointment(appointment.id);
        addToast('Appointment cancelled & email sent!', 'success');
      } else {
        await apiService.updateAppointment(appointment.id, { status: newStatus });
        if (newStatus === 'confirmed') {
          addToast('Appointment confirmed & email sent!', 'success');
        } else {
          addToast(`Appointment status updated to ${newStatus}`, 'success');
        }
      }
      
      onUpdate();
    } catch (err) {
      console.error('Error updating appointment:', err);
      setError('Failed to update appointment');
      addToast('Failed to update appointment', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.deleteAppointment(appointment.id);
      onUpdate();
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Failed to delete appointment');
    } finally {
      setLoading(false);
    }
  };

  const openGoogleCalendar = () => {
    if (appointment.google_calendar_event_id) {
      const eventUrl = `https://calendar.google.com/calendar/event?eid=${appointment.google_calendar_event_id}`;
      window.open(eventUrl, '_blank');
    }
  };

  const formatPhoneNumber = (phone: string) => {
    // Basic phone number formatting for display
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-purple-100 text-purple-800';
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'confirmed':
        return 'Confirmed';
      case 'scheduled':
        return 'Scheduled';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No Show';
      default:
        return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-xl font-bold text-gray-900">Appointment Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none ${activeTab === 'details' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none flex items-center gap-2 ${activeTab === 'intake' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('intake')}
            >
              Erhebungsbogen
              {appointment.intake_status === 'submitted' && (
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" aria-label="ausgefüllt"></span>
              )}
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm focus:outline-none ${activeTab === 'history' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('history')}
            >
              Historie
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {activeTab === 'intake' && <IntakeAdminPanel appointment={appointment} />}

          {activeTab === 'details' && (
            <>
              {/* Website-booking banner */}
          {appointment.source === 'website' && (
            <div className="mb-4 p-4 bg-purple-50 border-2 border-purple-300 rounded-lg">
              <div className="flex items-start gap-3">
                <span className="text-2xl">🌐</span>
                <div className="flex-1">
                  <h3 className="font-bold text-purple-900">Online-Buchung von seadent.es</h3>
                  <p className="text-sm text-purple-700">
                    {appointment.status === 'pending'
                      ? 'Patient hat über das Booking-Formular gebucht. Bitte bestätigen oder ablehnen.'
                      : 'Diese Buchung kam ursprünglich über die Website.'}
                  </p>
                  {appointment.insurance && (
                    <p className="text-sm mt-1 text-purple-700"><span className="font-medium">Versicherung (Patient-Angabe):</span> {appointment.insurance}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Patient Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Patient Information</h3>
              <div className="space-y-2">
                <p><span className="font-medium">Name:</span> {appointment.patient_name}</p>
                <p><span className="font-medium">Email:</span> {appointment.patient_email}</p>
                <p><span className="font-medium">Phone:</span> {formatPhoneNumber(appointment.patient_phone)}</p>
              </div>
            </div>

            {/* Appointment Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Appointment Details</h3>
              <div className="space-y-2">
                <p><span className="font-medium">Date:</span> {formatDate(appointment.appointment_date)}</p>
                <p><span className="font-medium">Time:</span> {formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}</p>
                <p><span className="font-medium">Provider:</span> {appointment.provider_name}</p>
                <p><span className="font-medium">Procedure:</span> {appointment.procedure_name}</p>
                <p><span className="font-medium">Operatory:</span> {appointment.operatory_name}</p>
                <p><span className="font-medium">Status:</span> 
                  <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
                    {getStatusLabel(appointment.status)}
                  </span>
                </p>
              </div>
            </div>

            {/* Notes */}
            {appointment.notes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                <p className="text-gray-700">{appointment.notes}</p>
              </div>
            )}

            {/* Google Calendar Link */}
            {appointment.google_calendar_event_id && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Google Calendar</h3>
                <button
                  onClick={openGoogleCalendar}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  View in Google Calendar
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Edit
            </button>

            {canComplete && (
              <button
                onClick={() => setShowCompletion(true)}
                className="px-4 py-2 bg-brand-primary text-white rounded-md hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary/40 font-semibold"
              >
                Behandlung abschließen
              </button>
            )}

            {canOfferQuote && (
              <button
                onClick={startOffer}
                className="px-4 py-2 border border-brand-primary text-brand-primary rounded-md hover:bg-brand-accent/10 focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
              >
                Angebot erstellen
              </button>
            )}
            
            {appointment.status === 'pending' && (
              <button
                onClick={() => handleStatusChange('scheduled')}
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
              >
                Accept (→ Scheduled)
              </button>
            )}

            {(appointment.status === 'scheduled' || appointment.status === 'pending') && (
              <button
                onClick={() => handleStatusChange('confirmed')}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                Confirm
              </button>
            )}
            
            {appointment.status === 'confirmed' && (
              <button
                onClick={() => handleStatusChange('completed')}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Complete
              </button>
            )}
            
            {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
              <button
                onClick={() => handleStatusChange('cancelled')}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            
            {appointment.status === 'confirmed' && (
              <button
                onClick={() => handleStatusChange('no_show')}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                No Show
              </button>
            )}
            
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                Delete
              </button>
            </div>
            </>
          )}

          {activeTab === 'history' && (
            <div className="space-y-4">
              {logsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading history...</div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">Keine Historie gefunden.</div>
              ) : (
                <div className="relative border-l border-gray-200 ml-3 space-y-6">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="mb-8 ml-6">
                      <span className="absolute flex items-center justify-center w-6 h-6 bg-blue-100 rounded-full -left-3 ring-8 ring-white">
                        <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                      </span>
                      <h3 className="flex items-center mb-1 text-md font-semibold text-gray-900">
                        {renderAuditAction(log)}
                      </h3>
                      <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                        {(() => {
                          const ts = log.changed_at || (log as any).created_at;
                          const d = ts ? new Date(ts) : null;
                          return d && !isNaN(d.getTime()) ? d.toLocaleString('de-DE') : '—';
                        })()} • durch {log.changed_by}
                      </time>
                      {renderAuditDetails(log)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showCompletion && (
        <CompletionDialog
          appointment={appointment}
          onClose={() => setShowCompletion(false)}
          onCompleted={() => { onUpdate(); }}
        />
      )}
    </div>
  );
};

export default AppointmentModal;